import { motion } from "framer-motion";
import { ShieldCheck, HeartHandshake, Award, MapPin, Building2 } from "lucide-react";
import Seo from "@/components/site/Seo";
import Marquee from "@/components/site/Marquee";
import QuoteButton from "@/components/site/QuoteButton";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { CONTACT, IMAGES } from "@/lib/site";

const VALUES = [
  {
    Icon: Award,
    title: "Quality you can trust",
    body: "We use premium materials and proven techniques for long-lasting performance on every installation.",
  },
  {
    Icon: ShieldCheck,
    title: "Expert installation",
    body: "Our skilled installers ensure a perfect fit and a flawless finish, every single time.",
  },
  {
    Icon: HeartHandshake,
    title: "Aftercare you can rely on",
    body: "We're here for you with dependable support and aftercare whenever you need it.",
  },
  {
    Icon: MapPin,
    title: "Local & reliable",
    body: `Proud to serve homes across ${CONTACT.area} with honesty and integrity.`,
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us | Eminence Windows & Doors, Greater Manchester"
        description="Eminence Windows & Doors combines premium products with expert installation, proudly serving homes across Greater Manchester with honesty and integrity."
      />

      {/* Hero */}
      <section className="relative flex min-h-[70vh] items-end overflow-hidden bg-charcoal" data-testid="about-hero">
        <img
          src={IMAGES.officeFacade}
          alt="Eminence Windows & Doors office — 82 King Street, Manchester"
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/60 to-charcoal/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 pt-40 lg:px-10">
          <p className="kicker text-gold">About Eminence</p>
          <h1 className="mt-6 font-serif text-5xl leading-[1] text-cream md:text-7xl">
            <MaskReveal lines={["Premium products,", "expertly installed."]} />
          </h1>
        </div>
      </section>

      {/* Intro */}
      <section className="mx-auto max-w-4xl px-5 py-24 lg:px-10 md:py-32" data-testid="about-intro">
        <Reveal>
          <p className="font-serif text-3xl leading-snug text-ink md:text-4xl">
            Based in the heart of Manchester, Eminence Windows &amp; Doors
            specialises in premium windows and doors, expertly supplied and
            installed across Greater Manchester.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 space-y-5 text-lg leading-relaxed text-stone">
            <p>
              We are a windows and doors specialist proudly serving homeowners
              across {CONTACT.area}. Our focus is simple: beautifully designed
              windows and doors that enhance your home inside and out, fitted to
              the highest standard.
            </p>
            <p>
              From your first enquiry to the final installation and beyond, our
              team is here to ensure a smooth, stress-free experience and complete
              peace of mind. We believe in dependable support, premium materials
              and a finish you'll be proud of for years to come.
            </p>
          </div>
        </Reveal>
      </section>

      {/* Our office */}
      <section className="bg-cream-deep py-24 md:py-32" data-testid="about-office">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-10">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl">
                <img
                  src={IMAGES.officeEntrance}
                  alt="Entrance to the Eminence Windows & Doors office on King Street, Manchester"
                  className="aspect-[4/3] w-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -right-4 hidden w-40 overflow-hidden rounded-2xl border-4 border-cream-deep shadow-xl sm:block lg:w-48">
                <img
                  src={IMAGES.officeDusk}
                  alt="The King Street building at dusk"
                  className="aspect-[3/4] w-full object-cover"
                />
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/15">
              <Building2 className="h-6 w-6 text-gold-deep" />
            </span>
            <p className="kicker mt-6 text-gold-deep">Our office</p>
            <h2 className="mt-3 font-serif text-4xl leading-tight text-ink md:text-5xl">
              In the heart of Manchester
            </h2>
            <p className="mt-5 max-w-md leading-relaxed text-stone">
              You'll find us on historic King Street, right in the centre of
              Manchester — a fitting home for a company built on craftsmanship
              and quality.
            </p>
            <address className="mt-6 not-italic font-serif text-2xl text-ink">
              {CONTACT.address}
            </address>
            <div className="mt-8">
              <QuoteButton size="md" testId="about-office-request-quote-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee
        dark
        items={["Comfort", "Style", "Security", "Energy Efficiency", "Peace of Mind", "CERTASS Certified Installers", "Expert Installation", "Greater Manchester", "Quality You Can Trust"]}
      />

      {/* Values */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-10 md:py-32" data-testid="about-values">
        <Reveal className="max-w-2xl">
          <p className="kicker text-gold-deep">What we stand for</p>
          <h2 className="mt-4 font-serif text-4xl leading-tight text-ink md:text-6xl">
            Crafted for quality. Installed with care.
          </h2>
        </Reveal>

        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full rounded-3xl border border-gold/20 bg-cream-card p-8 transition-colors hover:border-gold/50">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gold/15">
                  <v.Icon className="h-6 w-6 text-gold-deep" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-ink">{v.title}</h3>
                <p className="mt-3 leading-relaxed text-stone">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Area + CTA */}
      <section className="bg-charcoal py-24 text-cream md:py-28" data-testid="about-area">
        <div className="mx-auto flex max-w-4xl flex-col items-center px-5 text-center lg:px-10">
          <Reveal>
            <MapPin className="mx-auto h-8 w-8 text-gold" />
            <h2 className="mt-6 font-serif text-4xl md:text-5xl">
              Serving {CONTACT.area}
            </h2>
            <p className="mx-auto mt-5 max-w-xl leading-relaxed text-cream/75">
              Wherever you are in Greater Manchester, we'd love to help you find
              the perfect windows and doors for your home. Request a free,
              no-obligation quote today.
            </p>
            <div className="mt-9 flex justify-center">
              <QuoteButton size="lg" testId="about-request-quote-btn" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
